import {
  ClientPostTypeEnum,
  type ClientComment
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { IconButton, Text, TouchableRipple } from 'react-native-paper';

import { QuestionMarkIcon } from '@/assets';
import { usePopup } from '@/features/popup/provider/usePopup';
import { CommentEditorView } from '@/features/posts/components/comments/editor/CommentEditorView';
import { useVerification } from '@/features/verification/providers/useVerification';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surfaceContainerHighest,
      height: 80,
      width: 'auto',
      justifyContent: 'center',
      alignItems: 'center'
    },
    limited: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center'
    }
  });
type Props = {
  postId?: string;
  postType?: ClientPostTypeEnum;
  commentsAllowed: boolean;
  comment?: ClientComment;
  replyToComment?: ClientComment;
  onCancel: () => void;
  onCommentCreate: () => void;
};

export const CommentEditor: FC<Props> = ({
  postId,
  postType,
  comment,
  replyToComment,
  commentsAllowed,
  onCancel,
  onCommentCreate
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const { isVerified, showVerificationModal } = useVerification();
  const { showAlert } = usePopup();

  if (commentsAllowed) {
    return isVerified ? (
      <CommentEditorView
        postId={postId}
        comment={comment}
        replyToComment={replyToComment}
        onCancel={onCancel}
        onCommentCreate={onCommentCreate}
      />
    ) : (
      <TouchableRipple
        style={styles.container}
        onPress={() => showVerificationModal()}
      >
        <Text variant="bodyLarge" style={{ textAlign: 'center' }}>
          {t('notVerified')}
        </Text>
      </TouchableRipple>
    );
  }

  return (
    <View style={styles.container}>
      {postType === ClientPostTypeEnum.Suggestion ? (
        <View style={styles.limited}>
          <Text variant="bodyLarge">{t('commentsLimited')}</Text>
          <IconButton
            mode="outlined"
            size={12}
            onPress={() =>
              showAlert({
                dismissable: true,
                labels: t('alerts.commentsLimited', { returnObjects: true })
              })
            }
            style={{ borderWidth: 2 }}
            icon={({ color }) => (
              <QuestionMarkIcon height={16} width={16} fill={color} />
            )}
          />
        </View>
      ) : (
        <Text variant="bodyLarge" style={{ textAlign: 'center' }}>
          {t('commentsDisabled')}
        </Text>
      )}
    </View>
  );
};
